import { DirectUpload } from "@rails/activestorage"

// Browser → R2, with the Rails server only ever seeing the signed id.
//
// `directUploadUrl` is the club manager's own endpoint rather than Rails' stock
// /rails/active_storage/direct_uploads, because that is where the club being
// billed is known — see ClubManager::GalleryDirectUploadsController.

/** Uploads one file and resolves with the signed id to hand back to Rails. */
export function uploadFile(
  file: File,
  directUploadUrl: string,
  onProgress: (percent: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, directUploadUrl, {
      // The only hook that sees the XHR carrying the bytes, so the only place a
      // real progress figure can come from.
      directUploadWillStoreFileWithXHR(xhr) {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        })
      },
    })

    upload.create((error, blob) => {
      if (error) reject(error)
      else resolve(blob.signed_id)
    })
  })
}

/**
 * Runs `task` over every item, at most `limit` at a time, preserving input order
 * in the results.
 *
 * Firing two hundred uploads at once would have the browser queue them anyway,
 * but it would also put two hundred simultaneous token requests on the Rails
 * server for no gain. A handful in flight keeps the connection saturated without
 * that.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++
      results[index] = await task(items[index], index)
    }
  })

  await Promise.all(workers)
  return results
}
